import { FilterIcon } from "@heroicons/react/outline";
import { SearchIcon } from "@heroicons/react/solid";

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  getCreditState,
  getNoteState,
} from "../../../components/sessions/helper";
import NoteModel from "../../../api/models/NoteModel";
import { showApiErrorMessages } from "../../../helpers";
import AppFilters from "../../../components/app-filters/AppFilters";
import ShowIf from "../../../components/show-if/ShowIf";
import TableHeaderCell from "../../../components/table-cell/TableHeaderCell";
import { useOrderBy } from "../../../hooks/useOrderBy";
import { ApiCommonParams } from "../../../api/@types/apiTypes";
import Pagination from "../../../components/pagination/Pagination";
import { useQuery } from "react-query";
import { indexCredits } from "../../../api/endpoints/tutors/credits";
import CreditsModel from "../../../api/models/CreditsModel";
import UserProfilePictureAvatar from "../../../components/avatar/UserProfilePictureAvatar";
import { useInfiniteTutorStudents } from "../../../hooks/useInfiniteTutorStudents";

function FinanceViewPage() {
  const LocalBasePath = "page.tutor.dashboard.credits";
  const { t } = useTranslation("", { keyPrefix: LocalBasePath });
  const { orderBy, getSortingDirection, handleSetSortingOrder } = useOrderBy();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [searchVal, setSearchVal] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<{ [key: string]: any }>({});
  const { students, loadMoreStudents } = useInfiniteTutorStudents(true);

  let [currentPageFilter, setCurrentPageFilter] = useState<{
    [key: string]: any;
  }>({
    currentSessionsPage: 1,
  });
  const resetCurrentPage = () => {
    setCurrentPage(1);
  };

  const { data: payments } = useQuery(
    ["payments", currentPage, orderBy, searchVal],
    () => {
      let apiParams: ApiCommonParams = {
        orderBy,
        filters,
        search: searchVal,
        pagination: { pageSize: 10, pageNumber: currentPage },
      };
      return indexCredits(apiParams);
    },
    {
      keepPreviousData: true,
      select: (response) => {
        let fetchedCredits: CreditsModel[] = [];
        if (response.isSuccess) {
          fetchedCredits = CreditsModel.fromApiResponse(response.data?.history);
        } else {
          showApiErrorMessages(response);
        }
        const paginationInfo = response.pagination;
        return {
          data: fetchedCredits,
          amount: response.data?.total_amount ?? 0,
          paginationInfo,
        };
      },
    }
  );
  const onCancelFilters = () => {
    setShowFilters(false);
    setFilters({});
  };
  useEffect(() => {
    resetCurrentPage();
  }, [orderBy]);
  const studentsOptions = students.map((student) => ({
    key: student.name,
    value: `${student.id}`,
  }));

  const renderPayment = () => {
    return payments?.data.map((data) => {
      return (
        <tr className={"w-full border-t border-gray-100"}>
          <td className={"relative py-4 pl-4 sm:pl-6 pr-3 text-sm"}>
            <div className="font-normal text-gray-500">{data.description}</div>
          </td>
          <td className={" px-3 py-3.5  lg:table-cell"}>
            <div className="flex gap-3 pe-4">
              <UserProfilePictureAvatar
                profilePictureUrl={data.student?.profile_picture_url}
                className="w-10 h-10"
                alt="sand-user-img"
              />
              <div className="flex items-start gap-1 flex-col">
                <h3 className="text-sm font-medium text-gray-900">
                  {data.student?.firstName + " " + data.student?.lastName}
                </h3>
                <p className="text-xs font-normal text-gray-500">
                  {data.student?.account?.email}
                </p>
              </div>
            </div>
          </td>

          <td
            className={
              " px-3 py-3.5  table-cell text-xs  flex-nowrap flex-col  items-end font-medium  text-gray-500"
            }
          >
            {getCreditState(parseFloat(data.amount!.toString()))}
          </td>
        </tr>
      );
    });
  };
  return (
    <div className="w-full min-h-screen bg-white  flex flex-col  justify-between">
      <div className="px-4 md:px-6 flex flex-col justify-between  bg-gray-50 pb-6">
        <div className="py-5 text-gray-700 sm:flex sm:items-center sm:justify-between">
          <h2 className="text-2xl text-gray-700  flex flex-col gap-1  font-semibold">
            {t("myFinancialDues")}
            <p className="text-base font-semibold text-brandblue-600 ">
              {t("amountValue", { value: payments?.amount })}
            </p>
          </h2>
          <button
            type="button"
            data-cy="filterNotes"
            onClick={() => setShowFilters((x) => !x)}
            className="inline-flex items-center mt-4 md:mt-0 px-4 py-2 border border-gray-300 shadow-sm text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brandblue-500"
          >
            {t("filter")}
            <FilterIcon
              className="ml-3 -mr-1  rtl:mr-3 rtl:-ml-1 h-5 w-5 text-gray-500"
              aria-hidden="true"
            />
          </button>
        </div>
        <ShowIf condition={showFilters}>
          <AppFilters
            className="bg-gray-50 pb-5 flex flex-col sm:flex-row gap-4"
            filters={filters}
            setFilters={setFilters}
            onLoadMore={(key: string) =>
              setCurrentPageFilter({
                ...currentPageFilter,
                [key]: ++currentPageFilter[key],
              })
            }
            fields={[
              {
                name: "student_id",
                paginationLabel: "currentStudentsPage",
                label: t("students"),
                options: studentsOptions,
              },
            ]}
            handleButtonClick={onCancelFilters}
          />
        </ShowIf>
        <div className="relative rounded-md shadow-sm flex justify-between gap-[18px]">
          <div className="absolute inset-y-0 rtl:right-0 lrt:left-0 rtl:pr-3 ltr:pl-3  flex items-center pointer-events-none">
            <SearchIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
          </div>
          <input
            type="text"
            name="search"
            id="search"
            onChange={(e) => {
              setSearchVal(e.target.value);
            }}
            className="focus:ring-brandblue-500 focus:border-brandblue-500 block w-full rtl:pr-10 ltr:pl-10  sm:text-sm border-gray-300 rounded-md"
            placeholder={t("search")}
          />
        </div>
      </div>
      {payments?.data?.length! > 0 ? (
        <>
          <table
            data-cy="notesTable"
            className="w-full  hidden  sm:table-cell table-fixed  divide-gray-300 "
          >
            <thead className="bg-gray-100  w-screen border-y border-gray-200">
              <tr>
                <TableHeaderCell
                  className="  py-3 text-xs text-gray-500 font-medium text-justify w-[60%]"
                  sortable={true}
                  sortingDirection={getSortingDirection("session_id")}
                  onClick={handleSetSortingOrder("session_id")}
                >
                  {t("description")}
                </TableHeaderCell>
                <TableHeaderCell className="text-justify  py-3 text-xs text-gray-500 font-medium  w-[40%]">
                  {t("student")}
                </TableHeaderCell>
                <TableHeaderCell
                  className=" text-xs  py-3 text-gray-500 font-medium text-justify "
                  sortable={true}
                  sortingDirection={getSortingDirection("state")}
                  onClick={handleSetSortingOrder("state")}
                >
                  {t("amount")}
                </TableHeaderCell>
              </tr>
            </thead>
            <tbody>{renderPayment()}</tbody>
          </table>
          <div className="sm:hidden  w-full flex flex-col ">
            <div className="w-full bg-gray-100  px-4 py-3 border-b border-gray-200">
              <h4 className="text-gray-500 text-sm">{t("dues")}</h4>
            </div>
            <div className="w-full bg-white">
              <ul className="w-full">
                {payments?.data.map((payment: any, index: number) => {
                  return (
                    <li
                      key={`paymentInfoTable_${index}_note`}
                      className={
                        " border-b flex justify-between items-center w-full border-gray-100 pt-4 pb-1 px-2"
                      }
                    >
                      <div className="flex flex-col gap-1">
                        <div className="flex gap-3 items-center pe-4">
                          <UserProfilePictureAvatar
                            profilePictureUrl={
                              payment.student?.profile_picture_url
                            }
                            className="w-10 h-10"
                            alt="sand-user-img"
                          />
                          <div className="flex items-center gap-1 flex-col">
                            <h3 className="text-sm font-medium text-gray-900">
                              {payment.student?.firstName +
                                " " +
                                payment.student?.lastName}
                            </h3>
                          </div>
                        </div>

                        <div className={"relative my-3 text-sm"}>
                          <p className="font-normal text-gray-500 w-full">
                            {payment.description}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-nowrap  items-center justify-end w-[45%]">
                        {getCreditState(+payment?.amount)}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="flex h-[65vh] justify-center  items-center">
            <h1>{t("noresultsfound")}</h1>
          </div>
        </>
      )}

      <div className="sticky mt-auto bottom-0">
        <Pagination
          hasNext={!!payments?.paginationInfo?.hasNext}
          hasPrev={!!payments?.paginationInfo?.hasPrev}
          goToPage={(page: number) => setCurrentPage(page)}
          currentPage={Number(payments?.paginationInfo?.currentPageNumber || 0)}
          numberOfPages={Number(payments?.paginationInfo?.pagesCount || 0)}
        />
      </div>
    </div>
  );
}
export default FinanceViewPage;
